import {z} from 'zod'

import {engagementLevelValues} from './analytics/common'

/**
 * Represents the prospect list items that are returned by the API.
 */
const ProspectItemRawSchema = z.object({
    id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    capacity_bucket: z.nullable(z.string()),
    engagement_level: z.nullable(z.enum(engagementLevelValues)),
    estimated_capacity: z.nullable(z.string()),
    total_gift_amount: z.nullable(z.string()),
    is_assigned: z.boolean(),
    is_leadership: z.boolean(),
    assessed: z.nullable(z.string()),
    ds_rating: z.nullable(z.string()),
    manager_name: z.nullable(z.string()),
})

/**
 * Represents a prospect detail item that is returned by the API.
 */
const ProspectDetailRawSchema = ProspectItemRawSchema.extend({
    source_unique_id: z.nullable(z.string()),
    middle_name: z.nullable(z.string()),
    address_line_1: z.nullable(z.string()),
    address_line_2: z.nullable(z.string()),
    city: z.nullable(z.string()),
    state: z.nullable(z.string()),
    zip_code: z.nullable(z.string()),
    foundation: z.nullable(z.string()),
    real_estate_est: z.nullable(z.string()),
    sec_stock_value: z.nullable(z.string()),
    sec_stock_or_insider: z.boolean(),
    business_revenue: z.nullable(z.string()),
    business_affiliation: z.nullable(z.string()),
    median_household_income: z.nullable(z.number()),
    last_gift_date: z.nullable(z.string()),
    total_charitable_giving: z.nullable(z.string()),
    total_political_giving: z.nullable(z.string()),
    num_gifts: z.nullable(z.number()),
    largest_gift_amount: z.nullable(z.string()),
    largest_gift_date: z.nullable(z.string()),
    first_date_range: z.nullable(z.string()),
    first_gift_amount: z.nullable(z.string()),
})

/**
 * Represents a prospect list item transformed into camelCase.
 */
const ProspectItemSchema = ProspectItemRawSchema.transform(
    ({
        first_name,
        last_name,
        capacity_bucket,
        engagement_level,
        estimated_capacity,
        total_gift_amount,
        is_assigned,
        is_leadership,
        ds_rating,
        manager_name,
        ...rest
    }) => ({
        ...rest,
        firstName: first_name,
        lastName: last_name,
        capacityBucket: capacity_bucket,
        engagementLevel: engagement_level,
        estimatedCapacity: estimated_capacity,
        totalGiftAmount: total_gift_amount,
        isAssigned: is_assigned,
        isLeadership: is_leadership,
        dsRating: ds_rating,
        managerName: manager_name,
    }),
)

/**
 * Represents a prospect detail item transformed into camelCase.
 */
const ProspectDetailSchema = ProspectDetailRawSchema.transform(
    ({
        first_name,
        last_name,
        capacity_bucket,
        engagement_level,
        estimated_capacity,
        total_gift_amount,
        is_assigned,
        is_leadership,
        ds_rating,
        manager_name,
        source_unique_id,
        middle_name,
        address_line_1,
        address_line_2,
        city,
        state,
        zip_code,
        real_estate_est,
        sec_stock_value,
        sec_stock_or_insider,
        business_revenue,
        business_affiliation,
        median_household_income,
        last_gift_date,
        total_charitable_giving,
        total_political_giving,
        largest_gift_amount,
        largest_gift_date,
        first_date_range,
        first_gift_amount,
        num_gifts,
        ...rest
    }) => ({
        ...rest,
        firstName: first_name,
        lastName: last_name,
        capacityBucket: capacity_bucket,
        engagementLevel: engagement_level,
        estimatedCapacity: estimated_capacity,
        totalGiftAmount: total_gift_amount,
        isAssigned: is_assigned,
        isLeadership: is_leadership,
        dsRating: ds_rating,
        managerName: manager_name,
        sourceUniqueId: source_unique_id,
        middleName: middle_name,
        addressLine1: address_line_1,
        addressLine2: address_line_2,
        city,
        state,
        zipCode: zip_code,
        realEstateEst: real_estate_est,
        secStockValue: sec_stock_value,
        secStockOrInsider: sec_stock_or_insider,
        businessRevenue: business_revenue,
        businessAffiliation: business_affiliation,
        medianHouseholdIncome: median_household_income,
        lastGiftDate: last_gift_date,
        totalCharitableGiving: total_charitable_giving,
        totalPoliticalGiving: total_political_giving,
        largestGiftAmount: largest_gift_amount,
        largestGiftDate: largest_gift_date,
        firstDateRange: first_date_range,
        firstGiftAmount: first_gift_amount,
        numGifts: num_gifts,
    }),
)

/**
 * Represents the prospect list response that is returned by the API.
 */
export const ProspectsResponseSchema = z
    .object({
        total: z.number(),
        page: z.number(),
        page_size: z.number(),
        items: z.array(ProspectItemRawSchema),
    })
    .transform(({page_size, items, ...rest}) => ({
        ...rest,
        pageSize: page_size,
        items: items.map((item) => ProspectItemSchema.parse(item)),
    }))

export type ProspectsResponse = z.infer<typeof ProspectsResponseSchema>
export type ProspectDetail = z.infer<typeof ProspectDetailSchema>
export {ProspectDetailSchema}
